import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Box, Skeleton, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MlmRepository from "../../../Repositories/MlmRepository";
import { MlmDashboardLevel, MlmDashboardStats } from "../../../Objects/interfaces/MlmTypes";
import { mlmLevelColor } from "../../../App/Settings/StepSettings/Mlm/mlmUtils";
import useTailwindBrand from "../../../Theme/useTailwindBrand";
import ButtonField from "../../Ui/ButtonField";

type Period = "month" | "all";

interface Props {
    teachrId: number;
    /** Ouvre l'Academy (page de parrainage) pour le détail nominatif. */
    onNavigate?: () => void;
}

const euro = (value: number): string =>
    `${value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export const validatedOf = (level: Pick<MlmDashboardLevel, "validatedMonth" | "validatedTotal">, period: Period): number =>
    period === "month" ? level.validatedMonth : level.validatedTotal;

export const pendingOf = (level: Pick<MlmDashboardLevel, "pendingMonth" | "pendingTotal">, period: Period): number =>
    period === "month" ? level.pendingMonth : level.pendingTotal;

/**
 * Accueil Verified Trainer : « Mon réseau ». Filleuls par niveau (1 à 5) et
 * commissions du coach par niveau, en ne comptant que les commissions
 * validées ou payées ; les commissions en attente sont rappelées à part pour
 * expliquer l'écart avec le ledger. Vue « ce mois » par défaut, « depuis le
 * début » au choix. La liste nominative reste dans l'Academy.
 */
const MlmNetworkWidget: React.FC<Props> = ({ teachrId, onNavigate }) => {
    const { brandConfig } = useTailwindBrand();
    const primary = brandConfig.colors.primary;
    const ff = brandConfig.fontFamily;
    const [period, setPeriod] = useState<Period>("month");

    const { data, isLoading, isError } = useQuery<MlmDashboardStats>({
        queryKey: ["mlm-dashboard", teachrId],
        queryFn: () => MlmRepository.getDashboardStats(teachrId),
        enabled: !!teachrId,
        staleTime: 60_000,
    });

    const totalValidated = data ? validatedOf(data.totals, period) : 0;
    const totalPending = data ? pendingOf(data.totals, period) : 0;
    const periodLabel = period === "month" ? "ce mois" : "depuis le début";

    return (
        <Box sx={{ px: { xs: 2.5, md: 3 }, py: 3, borderTop: "1px solid", borderColor: "divider" }} data-testid="mlm-network-widget">
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" gap={1.5} mb={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ p: 0.6, borderRadius: 1.5, bgcolor: `${primary}15`, display: "flex" }}>
                        <GroupsOutlinedIcon sx={{ color: primary, fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={800} sx={{ fontFamily: ff, color: primary, lineHeight: 1.1 }}>
                            Mon réseau
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary" }}>
                            Filleuls par niveau et commissions acquises, {periodLabel}
                        </Typography>
                    </Box>
                </Stack>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={period}
                    onChange={(_, value: Period | null) => value && setPeriod(value)}
                    aria-label="Période des commissions"
                    sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontFamily: ff, fontSize: "0.72rem", py: 0.4, px: 1.2 } }}
                >
                    <ToggleButton value="month">Ce mois</ToggleButton>
                    <ToggleButton value="all">Depuis le début</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {isLoading && (
                <Stack spacing={1}>
                    {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={36} />)}
                </Stack>
            )}

            {!isLoading && isError && (
                <Typography variant="body2" sx={{ fontFamily: ff, color: "text.secondary" }}>
                    Les statistiques de votre réseau sont indisponibles pour le moment.
                </Typography>
            )}

            {!isLoading && data && (
                <>
                    {/* Totaux : filleuls, commissions acquises, en attente */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                        <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: `${primary}0d`, border: "1px solid", borderColor: `${primary}25` }}>
                            <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary", display: "block" }}>Filleuls</Typography>
                            <Typography variant="h6" fontWeight={800} sx={{ fontFamily: ff, color: primary, lineHeight: 1.2 }}>{data.networkSize}</Typography>
                        </Box>
                        <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: "#ecfdf5", border: "1px solid", borderColor: "#a7f3d0" }}>
                            <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary", display: "block" }}>Commissions acquises</Typography>
                            <Typography variant="h6" fontWeight={800} sx={{ fontFamily: ff, color: "#047857", lineHeight: 1.2 }} data-testid="mlm-validated-total">{euro(totalValidated)}</Typography>
                        </Box>
                        <Tooltip title="Commissions de cours déjà validés par l'URSSAF mais pas encore payés : elles seront acquises au paiement du cours.">
                            <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid", borderColor: "#fde68a" }}>
                                <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary", display: "block" }}>En attente</Typography>
                                <Typography variant="h6" fontWeight={800} sx={{ fontFamily: ff, color: "#b45309", lineHeight: 1.2 }} data-testid="mlm-pending-total">{euro(totalPending)}</Typography>
                            </Box>
                        </Tooltip>
                    </div>

                    {/* Un rang par niveau */}
                    <ul className="divide-y divide-gray-100">
                        {data.levels.map((level) => {
                            const color = mlmLevelColor(level.level);
                            const validated = validatedOf(level, period);
                            const pending = pendingOf(level, period);
                            return (
                                <li key={level.level} className="flex items-center gap-3 py-1.5" data-testid={`mlm-level-${level.level}`}>
                                    <span
                                        className="shrink-0 w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center"
                                        style={{ background: color }}
                                    >
                                        {level.level}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: ff, lineHeight: 1.2 }}>
                                            {level.legacy ? `Ancien niveau ${level.level}` : `Niveau ${level.level}`}
                                            {level.rate != null && <span className="text-gray-400 font-normal"> · {level.rate} %</span>}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary" }}>
                                            {level.legacy
                                                ? "Commissions acquises avant le 1er novembre 2026"
                                                : `${level.referrals} filleul${level.referrals > 1 ? "s" : ""}`}
                                            {pending > 0 ? `, ${euro(pending)} en attente` : ""}
                                        </Typography>
                                    </div>
                                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: ff, color: validated > 0 ? "#047857" : "text.disabled" }}>
                                        {euro(validated)}
                                    </Typography>
                                </li>
                            );
                        })}
                    </ul>

                    {data.networkSize === 0 && (
                        <Typography variant="caption" sx={{ fontFamily: ff, color: "text.secondary", display: "block", mt: 1 }}>
                            Aucun filleul pour l'instant : invitez des coachs avec votre code de parrainage, vos commissions apparaîtront ici dès leurs premiers cours payés.
                        </Typography>
                    )}

                    {onNavigate && (
                        <Box mt={2} display="flex" justifyContent="flex-end">
                            <ButtonField
                                label="Voir mon équipe"
                                variant="outlined"
                                size="small"
                                onClick={onNavigate}
                                endIcon={<ArrowForwardIcon />}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default MlmNetworkWidget;
